// -----------------------------------------------------------------------------
// core/commands/clip-commands.ts — undoable operations on clips.
//
// Add / remove / move / trim / split, plus editing a text overlay's properties.
// Each class captures what it needs to undo itself during do(). These are the
// timeline's vocabulary — the UI never mutates clips directly, it dispatches one
// of these. (CLAUDE.md §4/§6 Tier 0: "commands for clip+track".)
// -----------------------------------------------------------------------------
import type { Command } from '../commands';
import type { EditorState } from '../store';
import {
  type Clip,
  type ID,
  type MediaClip,
  type TextClip,
  clipEnd,
  findTrack,
  locateClip,
  newId,
  sortTrackClips,
} from '../model';

// Add an existing clip object to a track.
export class AddClipCommand implements Command {
  readonly label = 'Add clip';
  constructor(
    private readonly trackId: ID,
    private readonly clip: Clip,
  ) {}
  do(state: EditorState): void {
    const track = findTrack(state.project, this.trackId);
    if (!track) return;
    track.clips.push(this.clip);
    sortTrackClips(track);
  }
  undo(state: EditorState): void {
    const track = findTrack(state.project, this.trackId);
    if (!track) return;
    track.clips = track.clips.filter((c) => c.id !== this.clip.id);
  }
}

// Remove a clip; remembers its track + index so undo restores it exactly.
export class RemoveClipCommand implements Command {
  readonly label = 'Remove clip';
  private removed?: { trackId: ID; clip: Clip; index: number };
  private clearedSelection?: ID;
  constructor(private readonly clipId: ID) {}
  do(state: EditorState): void {
    const loc = locateClip(state.project, this.clipId);
    if (!loc) return;
    // Snapshot the clip so any later edits can't bleed into the undo state.
    this.removed = { trackId: loc.track.id, clip: structuredClone(loc.clip), index: loc.index };
    loc.track.clips.splice(loc.index, 1);
    // Don't leave the selection pointing at a clip that no longer exists.
    if (state.selectedClipId === this.clipId) {
      this.clearedSelection = this.clipId;
      state.selectedClipId = null;
    }
  }
  undo(state: EditorState): void {
    if (!this.removed) return;
    const track = findTrack(state.project, this.removed.trackId);
    if (!track) return;
    track.clips.splice(this.removed.index, 0, this.removed.clip);
    sortTrackClips(track);
    if (this.clearedSelection) {
      state.selectedClipId = this.clearedSelection;
      this.clearedSelection = undefined;
    }
  }
}

// Move a clip to a (possibly different) track and a new start time.
export class MoveClipCommand implements Command {
  readonly label = 'Move clip';
  private prev?: { trackId: ID; startSec: number };
  constructor(
    private readonly clipId: ID,
    private readonly toTrackId: ID,
    private readonly newStartSec: number,
  ) {}
  do(state: EditorState): void {
    const loc = locateClip(state.project, this.clipId);
    if (!loc) return;
    this.prev = { trackId: loc.track.id, startSec: loc.clip.startSec };
    loc.track.clips.splice(loc.index, 1);
    loc.clip.startSec = Math.max(0, this.newStartSec);
    const dest = findTrack(state.project, this.toTrackId) ?? loc.track;
    dest.clips.push(loc.clip);
    sortTrackClips(dest);
  }
  undo(state: EditorState): void {
    if (!this.prev) return;
    const loc = locateClip(state.project, this.clipId);
    if (!loc) return;
    loc.track.clips.splice(loc.index, 1);
    loc.clip.startSec = this.prev.startSec;
    const orig = findTrack(state.project, this.prev.trackId) ?? loc.track;
    orig.clips.push(loc.clip);
    sortTrackClips(orig);
  }
}

export interface TrimValues {
  startSec: number;
  inSec: number; // ignored for text clips
  durationSec: number;
}

// Trim a clip's edges: changes start/in-point/duration together. The caller
// (timeline interactions) computes clamped values; this just applies + inverts.
export class TrimClipCommand implements Command {
  readonly label = 'Trim clip';
  private prev?: TrimValues;
  constructor(
    private readonly clipId: ID,
    private readonly next: TrimValues,
  ) {}
  do(state: EditorState): void {
    const loc = locateClip(state.project, this.clipId);
    if (!loc) return;
    const c = loc.clip;
    this.prev = {
      startSec: c.startSec,
      inSec: c.kind === 'text' ? 0 : c.inSec,
      durationSec: c.durationSec,
    };
    c.startSec = this.next.startSec;
    c.durationSec = this.next.durationSec;
    if (c.kind !== 'text') c.inSec = this.next.inSec;
    sortTrackClips(loc.track);
  }
  undo(state: EditorState): void {
    if (!this.prev) return;
    const loc = locateClip(state.project, this.clipId);
    if (!loc) return;
    const c = loc.clip;
    c.startSec = this.prev.startSec;
    c.durationSec = this.prev.durationSec;
    if (c.kind !== 'text') c.inSec = this.prev.inSec;
    sortTrackClips(loc.track);
  }
}

// Split a clip at a timeline time into two clips. The left half keeps the
// original id; a fresh right half is inserted after it.
export class SplitClipCommand implements Command {
  readonly label = 'Split clip';
  private original?: Clip; // snapshot to restore on undo
  private trackId?: ID;
  private index?: number;
  private rightId?: ID;
  constructor(
    private readonly clipId: ID,
    private readonly atSec: number,
  ) {}
  do(state: EditorState): void {
    const loc = locateClip(state.project, this.clipId);
    if (!loc) return;
    const c = loc.clip;
    const end = clipEnd(c);
    // Need a sliver on each side, else there's nothing to split.
    if (this.atSec <= c.startSec + 1e-4 || this.atSec >= end - 1e-4) return;

    this.original = structuredClone(c);
    this.trackId = loc.track.id;
    this.index = loc.index;

    const leftDuration = this.atSec - c.startSec;
    const right = structuredClone(c) as Clip;
    right.id = newId();
    right.startSec = this.atSec;
    right.durationSec = end - this.atSec;
    if (right.kind !== 'text') (right as MediaClip).inSec += leftDuration;
    this.rightId = right.id;

    c.durationSec = leftDuration; // shrink the left half
    loc.track.clips.splice(loc.index + 1, 0, right);
    sortTrackClips(loc.track);
  }
  undo(state: EditorState): void {
    if (!this.original || !this.trackId) return;
    const track = findTrack(state.project, this.trackId);
    if (!track) return;
    track.clips = track.clips.filter((c) => c.id !== this.clipId && c.id !== this.rightId);
    track.clips.splice(this.index ?? track.clips.length, 0, this.original);
    sortTrackClips(track);
  }
}

export type TextPatch = Partial<
  Pick<TextClip, 'text' | 'fontSize' | 'color' | 'fontFamily' | 'xFrac' | 'yFrac'>
>;

// Edit one or more properties of a text overlay clip.
export class UpdateTextClipCommand implements Command {
  readonly label = 'Edit text';
  private prev: TextPatch = {};
  constructor(
    private readonly clipId: ID,
    private readonly patch: TextPatch,
  ) {}
  do(state: EditorState): void {
    const loc = locateClip(state.project, this.clipId);
    if (!loc || loc.clip.kind !== 'text') return;
    const clip = loc.clip;
    for (const key of Object.keys(this.patch) as (keyof TextPatch)[]) {
      (this.prev as Record<string, unknown>)[key] = clip[key];
      (clip as unknown as Record<string, unknown>)[key] = this.patch[key];
    }
  }
  undo(state: EditorState): void {
    const loc = locateClip(state.project, this.clipId);
    if (!loc || loc.clip.kind !== 'text') return;
    const clip = loc.clip;
    for (const key of Object.keys(this.prev) as (keyof TextPatch)[]) {
      (clip as unknown as Record<string, unknown>)[key] = this.prev[key];
    }
  }
}
